/**
 * InteractiveDrillDown Component
 * Multi-level drill-down analysis system for exploring compressor data
 * Provides hierarchical navigation through data layers
 */

import { useState, useMemo, useCallback } from 'react'
import {
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  Search,
  Filter,
  Layers,
  AlertTriangle,
  CheckCircle,
  Info,
  TrendingUp,
  TrendingDown,
  Activity,
  Zap,
  Clock,
  Target,
  BarChart3,
  GitBranch,
  Box,
  Cpu,
  Database
} from 'lucide-react'
import clsx from 'clsx'

// Drill-down level types
type DrillLevel = 'fleet' | 'asset' | 'system' | 'component' | 'sensor' | 'reading'

// Breadcrumb item
interface BreadcrumbItem {
  level: DrillLevel
  id: string
  label: string
}

// Generic drill-down node
interface DrillNode {
  id: string
  label: string
  type: DrillLevel
  status: 'healthy' | 'warning' | 'critical' | 'unknown'
  value?: number
  unit?: string
  trend?: 'up' | 'down' | 'stable'
  trendValue?: number
  children?: DrillNode[]
  metadata?: Record<string, string | number>
  insights?: string[]
  lastUpdated?: string
}

// Detail panel data
interface DetailData {
  title: string
  description: string
  metrics: Array<{
    label: string
    value: string | number
    unit?: string
    status?: 'good' | 'warning' | 'critical'
    trend?: 'up' | 'down' | 'stable'
  }>
  timeline?: Array<{
    timestamp: string
    event: string
    type: 'info' | 'warning' | 'critical' | 'success'
  }>
  relatedItems?: Array<{
    id: string
    label: string
    type: string
    status: 'healthy' | 'warning' | 'critical'
  }>
  recommendations?: string[]
}

interface InteractiveDrillDownProps {
  data: DrillNode[]
  onNodeSelect?: (node: DrillNode, path: BreadcrumbItem[]) => void
  onDetailRequest?: (nodeId: string, level: DrillLevel) => Promise<DetailData | null>
  initialPath?: BreadcrumbItem[]
  className?: string
}

export default function InteractiveDrillDown({
  data,
  onNodeSelect,
  onDetailRequest,
  initialPath = [],
  className
}: InteractiveDrillDownProps) {
  const [currentPath, setCurrentPath] = useState<BreadcrumbItem[]>(initialPath)
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())
  const [selectedNode, setSelectedNode] = useState<DrillNode | null>(null)
  const [detailData, setDetailData] = useState<DetailData | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | null>(null)

  // Get current level nodes based on path
  const currentNodes = useMemo(() => {
    if (currentPath.length === 0) return data

    let nodes = data
    for (const item of currentPath) {
      const found = nodes.find(n => n.id === item.id)
      if (found?.children) {
        nodes = found.children
      } else {
        return []
      }
    }
    return nodes
  }, [data, currentPath])

  // Filter nodes based on search and status
  const filteredNodes = useMemo(() => {
    return currentNodes.filter(node => {
      const matchesSearch = !searchQuery ||
        node.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        node.id.toLowerCase().includes(searchQuery.toLowerCase())
      const matchesStatus = !statusFilter || node.status === statusFilter
      return matchesSearch && matchesStatus
    })
  }, [currentNodes, searchQuery, statusFilter])

  // Navigate to a node
  const navigateToNode = useCallback(async (node: DrillNode) => {
    const newPath = [...currentPath, { level: node.type, id: node.id, label: node.label }]
    setCurrentPath(newPath)
    setSelectedNode(null)
    setDetailData(null)
    onNodeSelect?.(node, newPath)
  }, [currentPath, onNodeSelect])

  // Navigate back via breadcrumb
  const navigateToBreadcrumb = useCallback((index: number) => {
    setCurrentPath(currentPath.slice(0, index))
    setSelectedNode(null)
    setDetailData(null)
  }, [currentPath])

  // Select a node for detail view
  const selectNode = useCallback(async (node: DrillNode) => {
    setSelectedNode(node)
    if (onDetailRequest) {
      setDetailLoading(true)
      try {
        const detail = await onDetailRequest(node.id, node.type)
        setDetailData(detail)
      } catch {
        setDetailData(null)
      } finally {
        setDetailLoading(false)
      }
    }
  }, [onDetailRequest])

  // Toggle node expansion
  const toggleExpand = useCallback((nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev)
      if (next.has(nodeId)) {
        next.delete(nodeId)
      } else {
        next.add(nodeId)
      }
      return next
    })
  }, [])

  // Get icon for level type
  const getLevelIcon = (level: DrillLevel) => {
    switch (level) {
      case 'fleet': return <Database size={16} />
      case 'asset': return <Box size={16} />
      case 'system': return <GitBranch size={16} />
      case 'component': return <Cpu size={16} />
      case 'sensor': return <Activity size={16} />
      case 'reading': return <BarChart3 size={16} />
      default: return <Layers size={16} />
    }
  }

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-status-healthy'
      case 'warning': return 'text-status-warning'
      case 'critical': return 'text-status-critical'
      default: return 'text-surface-400'
    }
  }

  // Render a drill node
  const renderNode = (node: DrillNode, depth: number = 0) => {
    const hasChildren = node.children && node.children.length > 0
    const isExpanded = expandedNodes.has(node.id)
    const isSelected = selectedNode?.id === node.id

    return (
      <div key={node.id} className="select-none">
        <div
          className={clsx(
            'flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-all duration-200',
            'hover:bg-surface-700/50',
            isSelected && 'bg-primary-500/20 border border-primary-500/30',
            !isSelected && 'border border-transparent'
          )}
          style={{ paddingLeft: `${depth * 16 + 12}px` }}
        >
          {/* Expand/Collapse button */}
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation()
                toggleExpand(node.id)
              }}
              className="p-1 hover:bg-surface-600 rounded"
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : (
            <div className="w-6" />
          )}

          {/* Node content - clickable for selection */}
          <div
            className="flex-1 flex items-center gap-3"
            onClick={() => selectNode(node)}
          >
            {/* Level icon */}
            <div className={clsx(
              'w-8 h-8 rounded-lg flex items-center justify-center',
              node.status === 'healthy' ? 'bg-status-healthy/20 text-status-healthy' :
              node.status === 'warning' ? 'bg-status-warning/20 text-status-warning' :
              node.status === 'critical' ? 'bg-status-critical/20 text-status-critical' :
              'bg-surface-600 text-surface-400'
            )}>
              {getLevelIcon(node.type)}
            </div>

            {/* Label and metadata */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-surface-100 truncate">{node.label}</span>
                <span className="text-xs text-surface-500">({node.id})</span>
              </div>
              {node.metadata && (
                <div className="flex items-center gap-3 mt-0.5">
                  {Object.entries(node.metadata).slice(0, 3).map(([key, value]) => (
                    <span key={key} className="text-xs text-surface-400">
                      {key}: <span className="text-surface-300">{value}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Value and trend */}
            {node.value !== undefined && (
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <div className={clsx('text-sm font-semibold', getStatusColor(node.status))}>
                    {typeof node.value === 'number' ? node.value.toFixed(1) : node.value}
                    {node.unit && <span className="text-xs text-surface-400 ml-1">{node.unit}</span>}
                  </div>
                  {node.trend && node.trendValue !== undefined && (
                    <div className="flex items-center justify-end gap-1 text-xs">
                      {node.trend === 'up' ? (
                        <TrendingUp size={10} className="text-status-warning" />
                      ) : node.trend === 'down' ? (
                        <TrendingDown size={10} className="text-status-healthy" />
                      ) : (
                        <Activity size={10} className="text-surface-400" />
                      )}
                      <span className={clsx(
                        node.trend === 'up' ? 'text-status-warning' :
                        node.trend === 'down' ? 'text-status-healthy' : 'text-surface-400'
                      )}>
                        {node.trendValue > 0 ? '+' : ''}{node.trendValue.toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Status indicator */}
            <div className={clsx(
              'w-2 h-2 rounded-full',
              node.status === 'healthy' ? 'bg-status-healthy' :
              node.status === 'warning' ? 'bg-status-warning' :
              node.status === 'critical' ? 'bg-status-critical' :
              'bg-surface-500'
            )} />
          </div>

          {/* Drill-down button */}
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                navigateToNode(node)
              }}
              className="p-2 hover:bg-surface-600 rounded-lg text-primary-400 hover:text-primary-300 transition-colors"
              title="Drill down"
            >
              <ChevronRight size={16} />
            </button>
          )}
        </div>

        {/* Expanded children */}
        {hasChildren && isExpanded && (
          <div className="border-l border-surface-700 ml-6">
            {node.children!.map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  // Render detail panel
  const renderDetailPanel = () => {
    if (detailLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-2" />
            <p className="text-sm text-surface-400">Loading details...</p>
          </div>
        </div>
      )
    }

    if (!selectedNode) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Layers size={48} className="mx-auto text-surface-600 mb-3" />
            <p className="text-surface-400">Select an item to view details</p>
            <p className="text-xs text-surface-500 mt-1">Click on any node in the tree</p>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {/* Node Header */}
        <div className="flex items-start gap-3">
          <div className={clsx(
            'w-12 h-12 rounded-xl flex items-center justify-center',
            selectedNode.status === 'healthy' ? 'bg-status-healthy/20 text-status-healthy' :
            selectedNode.status === 'warning' ? 'bg-status-warning/20 text-status-warning' :
            selectedNode.status === 'critical' ? 'bg-status-critical/20 text-status-critical' :
            'bg-surface-600 text-surface-400'
          )}>
            {getLevelIcon(selectedNode.type)}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-surface-100">{selectedNode.label}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-surface-500">{selectedNode.type.toUpperCase()}</span>
              <span className="text-surface-600">|</span>
              <span className="text-xs text-surface-400">{selectedNode.id}</span>
            </div>
          </div>
          <div className={clsx(
            'px-3 py-1 rounded-full text-xs font-medium',
            selectedNode.status === 'healthy' ? 'bg-status-healthy/20 text-status-healthy' :
            selectedNode.status === 'warning' ? 'bg-status-warning/20 text-status-warning' :
            selectedNode.status === 'critical' ? 'bg-status-critical/20 text-status-critical' :
            'bg-surface-600 text-surface-400'
          )}>
            {selectedNode.status.charAt(0).toUpperCase() + selectedNode.status.slice(1)}
          </div>
        </div>

        {/* Primary Value */}
        {selectedNode.value !== undefined && (
          <div className="bg-surface-800/50 rounded-lg p-4 border border-surface-700">
            <div className="text-xs text-surface-400 mb-1">Current Value</div>
            <div className="flex items-end gap-2">
              <span className={clsx('text-3xl font-bold', getStatusColor(selectedNode.status))}>
                {typeof selectedNode.value === 'number' ? selectedNode.value.toFixed(2) : selectedNode.value}
              </span>
              {selectedNode.unit && (
                <span className="text-lg text-surface-400 mb-1">{selectedNode.unit}</span>
              )}
              {selectedNode.trend && selectedNode.trendValue !== undefined && (
                <div className="flex items-center gap-1 mb-1 ml-2">
                  {selectedNode.trend === 'up' ? (
                    <TrendingUp size={16} className="text-status-warning" />
                  ) : selectedNode.trend === 'down' ? (
                    <TrendingDown size={16} className="text-status-healthy" />
                  ) : (
                    <Activity size={16} className="text-surface-400" />
                  )}
                  <span className={clsx(
                    'text-sm font-medium',
                    selectedNode.trend === 'up' ? 'text-status-warning' :
                    selectedNode.trend === 'down' ? 'text-status-healthy' : 'text-surface-400'
                  )}>
                    {selectedNode.trendValue > 0 ? '+' : ''}{selectedNode.trendValue.toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Metadata */}
        {selectedNode.metadata && Object.keys(selectedNode.metadata).length > 0 && (
          <div className="bg-surface-800/50 rounded-lg p-4 border border-surface-700">
            <h4 className="text-sm font-medium text-surface-300 mb-3 flex items-center gap-2">
              <Database size={14} />
              Properties
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(selectedNode.metadata).map(([key, value]) => (
                <div key={key}>
                  <div className="text-xs text-surface-500">{key}</div>
                  <div className="text-sm text-surface-200">{value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Insights */}
        {selectedNode.insights && selectedNode.insights.length > 0 && (
          <div className="bg-gradient-to-br from-primary-500/10 to-cyan-500/10 rounded-lg p-4 border border-primary-500/20">
            <h4 className="text-sm font-medium text-primary-300 mb-3 flex items-center gap-2">
              <Zap size={14} />
              AI Insights
            </h4>
            <ul className="space-y-2">
              {selectedNode.insights.map((insight, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-surface-200">
                  <CheckCircle size={14} className="text-primary-400 mt-0.5 flex-shrink-0" />
                  {insight}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Detail Data (from API) */}
        {detailData && (
          <>
            {/* Description */}
            {detailData.description && (
              <div className="text-sm text-surface-300 bg-surface-800/50 rounded-lg p-3">
                {detailData.description}
              </div>
            )}

            {/* Metrics Grid */}
            {detailData.metrics && detailData.metrics.length > 0 && (
              <div className="bg-surface-800/50 rounded-lg p-4 border border-surface-700">
                <h4 className="text-sm font-medium text-surface-300 mb-3 flex items-center gap-2">
                  <BarChart3 size={14} />
                  Metrics
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {detailData.metrics.map((metric, idx) => (
                    <div key={idx} className="bg-surface-900/50 rounded-lg p-3">
                      <div className="text-xs text-surface-500 mb-1">{metric.label}</div>
                      <div className="flex items-end gap-1">
                        <span className={clsx(
                          'text-lg font-semibold',
                          metric.status === 'good' ? 'text-status-healthy' :
                          metric.status === 'warning' ? 'text-status-warning' :
                          metric.status === 'critical' ? 'text-status-critical' : 'text-surface-100'
                        )}>
                          {metric.value}
                        </span>
                        {metric.unit && (
                          <span className="text-xs text-surface-400 mb-0.5">{metric.unit}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Timeline */}
            {detailData.timeline && detailData.timeline.length > 0 && (
              <div className="bg-surface-800/50 rounded-lg p-4 border border-surface-700">
                <h4 className="text-sm font-medium text-surface-300 mb-3 flex items-center gap-2">
                  <Clock size={14} />
                  Recent Events
                </h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {detailData.timeline.map((event, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <div className={clsx(
                        'w-2 h-2 rounded-full mt-1.5 flex-shrink-0',
                        event.type === 'info' ? 'bg-blue-500' :
                        event.type === 'warning' ? 'bg-yellow-500' :
                        event.type === 'critical' ? 'bg-red-500' : 'bg-green-500'
                      )} />
                      <div className="flex-1">
                        <div className="text-surface-200">{event.event}</div>
                        <div className="text-xs text-surface-500">{event.timestamp}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {detailData.recommendations && detailData.recommendations.length > 0 && (
              <div className="bg-status-warning/10 rounded-lg p-4 border border-status-warning/20">
                <h4 className="text-sm font-medium text-status-warning mb-3 flex items-center gap-2">
                  <Target size={14} />
                  Recommendations
                </h4>
                <ul className="space-y-2">
                  {detailData.recommendations.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-surface-200">
                      <AlertTriangle size={14} className="text-status-warning mt-0.5 flex-shrink-0" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {/* Last Updated */}
        {selectedNode.lastUpdated && (
          <div className="text-xs text-surface-500 text-center">
            Last updated: {selectedNode.lastUpdated}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={clsx('bg-surface-800 rounded-xl border border-surface-700 overflow-hidden', className)}>
      {/* Header */}
      <div className="p-4 border-b border-surface-700">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Layers size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-surface-100">Interactive Drill-Down Analysis</h3>
              <p className="text-sm text-surface-400">Explore data hierarchically</p>
            </div>
          </div>
        </div>

        {/* Breadcrumb Navigation */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => navigateToBreadcrumb(0)}
            className={clsx(
              'flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors',
              currentPath.length === 0
                ? 'bg-primary-500/20 text-primary-400'
                : 'text-surface-400 hover:text-surface-200 hover:bg-surface-700/50'
            )}
          >
            <Database size={14} />
            Root
          </button>
          {currentPath.map((item, idx) => (
            <div key={item.id} className="flex items-center gap-2">
              <ChevronRight size={14} className="text-surface-600" />
              <button
                onClick={() => navigateToBreadcrumb(idx + 1)}
                className={clsx(
                  'flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors',
                  idx === currentPath.length - 1
                    ? 'bg-primary-500/20 text-primary-400'
                    : 'text-surface-400 hover:text-surface-200 hover:bg-surface-700/50'
                )}
              >
                {getLevelIcon(item.level)}
                {item.label}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Toolbar */}
      <div className="p-3 bg-surface-900/30 border-b border-surface-700 flex items-center gap-3">
        {currentPath.length > 0 && (
          <button
            onClick={() => navigateToBreadcrumb(currentPath.length - 1)}
            className="flex items-center gap-1 px-2 py-1 rounded text-sm text-surface-400 hover:text-surface-200 hover:bg-surface-700/50 transition-colors"
          >
            <ArrowLeft size={14} />
            Back
          </button>
        )}
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 bg-surface-800 border border-surface-700 rounded-lg text-sm text-surface-100 placeholder:text-surface-500 focus:border-primary-500 focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-1">
          <Filter size={14} className="text-surface-500" />
          <select
            value={statusFilter || ''}
            onChange={(e) => setStatusFilter(e.target.value || null)}
            className="bg-surface-800 border border-surface-700 rounded-lg px-2 py-1.5 text-sm text-surface-200 focus:border-primary-500 focus:outline-none"
          >
            <option value="">All Status</option>
            <option value="healthy">Healthy</option>
            <option value="warning">Warning</option>
            <option value="critical">Critical</option>
          </select>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-12 divide-x divide-surface-700" style={{ height: '500px' }}>
        {/* Tree View */}
        <div className="col-span-7 overflow-y-auto p-2">
          {filteredNodes.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Info size={32} className="mx-auto text-surface-600 mb-2" />
                <p className="text-surface-400">No items found</p>
              </div>
            </div>
          ) : (
            filteredNodes.map(node => renderNode(node))
          )}
        </div>

        {/* Detail Panel */}
        <div className="col-span-5 overflow-y-auto p-4 bg-surface-900/30">
          {renderDetailPanel()}
        </div>
      </div>
    </div>
  )
}
